import { useState } from "react";
import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { ChevronDown, Check } from "lucide-react";

// #STYLE_GUIDE: TypeScript interfaces use camelCase (props are transformed by turbo-mount)
interface ExpiringLinkProps {
  id: string;
  slug: string;
  avatar: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  createdAt: string;
  updatedAt: string;
}

interface AvatarProps {
  id: string;
  displayName: string;
}

interface ExpiringLinkUpdateProps {
  expiringLink: ExpiringLinkProps;
  avatars: AvatarProps[];
}

// #STYLE_GUIDE: Update component for editing existing records (includes delete functionality)
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
// #TODO: This is a basic implementation - customize form fields and validation as needed
export default function ExpiringLinkUpdate({ expiringLink, avatars }: ExpiringLinkUpdateProps) {
  const [formData, setFormData] = useState({
    slug: expiringLink.slug || "",
    avatarId: expiringLink.avatarId || "",
    expiresAt: expiringLink.expiresAt || "",
  });

  const [selectedAvatar, setSelectedAvatar] = useState<AvatarProps | null>(
    avatars.find(avatar => avatar.id === expiringLink.avatarId) || null
  );
  const [avatarQuery, setAvatarQuery] = useState("");

  const { loading, errors, makeRequest } = useApiRequest({
    onSuccess: (data) => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit(`/expiring_links/${data.id}`);
    },
    onError: (error) => {
      console.error("Failed to update expiring_link:", error);
    },
  });

  const { loading: deleteLoading, makeRequest: makeDeleteRequest } = useApiRequest({
    onSuccess: () => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit("/expiring_links");
    },
    onError: (error) => {
      console.error("Failed to delete expiring_link:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      avatarId: selectedAvatar?.id || "",
    };

    await makeRequest("PUT", `/api/v1/expiring_links/${expiringLink.id}`, {
      expiringLink: submitData,
    });
  };

  const handleDelete = async () => {
    if (confirm("Are you sure you want to delete this expiring_link? This action cannot be undone.")) {
      await makeDeleteRequest("DELETE", `/api/v1/expiring_links/${expiringLink.id}`);
    }
  };

  const handleCancel = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/expiring_links/${expiringLink.id}`);
  };

  const getFieldError = (fieldName: string) => {
    return errors?.find((error: any) => error.field === fieldName)?.message;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Edit ExpiringLink</h1>
        <div className="join">
          <button
            onClick={handleCancel}
            className="btn btn-outline join-item"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="btn btn-error join-item"
            disabled={deleteLoading || loading}
          >
            {deleteLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Slug field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Slug</span>
              </label>
              <input
                type="text"
                className={`input input-bordered ${getFieldError('slug') ? 'input-error' : ''}`}
                placeholder="Enter slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  slug: e.target.value
                }))}
              />
              {getFieldError('slug') && (
                <label className="label">
                  <span className="label-text-alt text-error">{getFieldError('slug')}</span>
                </label>
              )}
            </div>

            {/* Avatar field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Avatar</span>
              </label>
              <div className="relative">
                <Listbox
                  value={selectedAvatar}
                  onChange={(value) => {
                    setSelectedAvatar(value);
                    setFormData(prev => ({
                      ...prev,
                      avatarId: value?.id || ""
                    }));
                  }}
                >
                  <ListboxButton className={`input input-bordered w-full text-left ${getFieldError('avatarId') ? 'input-error' : ''}`}>
                    {selectedAvatar?.displayName || "Select avatar"}
                    <ChevronDown className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none w-5 h-5" />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 w-full bg-base-100 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {(() => {
                      const filteredAvatar = avatars
                        .filter((avatar) =>
                          avatar.displayName.toLowerCase().includes(avatarQuery.toLowerCase())
                        );

                      if (filteredAvatar.length === 0) {
                        return (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No avatar found.
                          </div>
                        );
                      }

                      return filteredAvatar.map((avatar) => (
                        <ListboxOption
                          key={avatar.id}
                          value={avatar}
                          className="relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-base-200"
                        >
                          {avatar.displayName}
                          {selectedAvatar?.id === avatar.id && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </ListboxOption>
                      ));
                    })()}
                  </ListboxOptions>
                </Listbox>
              </div>
              {getFieldError('avatarId') && (
                <label className="label">
                  <span className="label-text-alt text-error">{getFieldError('avatarId')}</span>
                </label>
              )}
            </div>

            {/* Expires at field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Expires at</span>
              </label>
              <input
                type="datetime-local"
                className={`input input-bordered ${getFieldError('expiresAt') ? 'input-error' : ''}`}
                placeholder="Enter expires at"
                value={formData.expiresAt}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  expiresAt: e.target.value
                }))}
              />
              {getFieldError('expiresAt') && (
                <label className="label">
                  <span className="label-text-alt text-error">{getFieldError('expiresAt')}</span>
                </label>
              )}
            </div>

            {/* Submit buttons */}
            <div className="form-control mt-6">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={loading || deleteLoading}
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Updating...
                    </>
                  ) : (
                    "Update ExpiringLink"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline"
                  disabled={loading || deleteLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
